import { useState, useRef, useEffect, useCallback, useMemo, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import './App.css';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ChatbotSidebar from './components/ChatbotSidebar';
import DataGrid from './components/DataGrid';
import BlockConfigForm from './components/BlockConfigForm';
import LogoBlockConfig from './components/LogoBlockConfig';
import ExcelBackupAction, { saveToGoogleSheets } from './components/ExcelExporter';
import HtmlPreview from './components/HtmlPreview';
import BlockTabs from './components/BlockTabs';
import { Block } from './classes/Block';
import {
  CardGroup,
  CardId,
  generateCardId,
  initializeCardGroups,
  mergeCardWithPrevious,
  normalizeCardGroups,
  splitCardAtBlock,
} from './utils/cardGroups';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnifiedRender from './components/UnifiedRender';
import { VideoSettings as VideoSettingsType, DEFAULT_VIDEO_SETTINGS } from './components/VideoSettings';
import { DefaultCardSettings, DEFAULT_CARD_SETTINGS } from './utils/defaultCardSettings';
import { I18nProvider, useLingui } from '@lingui/react';
import AppLocaleProvider from './components/AppLocaleProvider';
import { i18n } from './i18n';
import { t } from '@lingui/core/macro';
import AuthGate from "./components/AuthGate";
import NavBar from './components/NavBar';
import { auth } from './firebase';
import { buildTableData } from './utils/excelTable';
import { getApiUrl } from './utils/api';
import { authFetch } from './utils/authFetch';
import { FontFamilyInfo, FontInfo, USER_UPLOADED_FONT_FAMILY } from './components/FontManagement';
import { LogoInfo } from './components/LogoManagement';
import { normalizeCardFramingSettings } from './utils/cardFraming';
import {
  FontMetadata,
  FontSettingsMap,
  FontSettingsEntry,
  VariableFontConfig,
  getFallbackVariableFontConfig,
  getFontEntryEnabled,
  getFontEntryUrl,
  getFontEntryVariableConfig,
} from './utils/fontHelpers';
import { FontFamilyOption } from './components/ReusableFontPicker';
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import ProjectSelector from './pages/ProjectSelector';
import Renders from './pages/Renders';
import ProjectSettings from './pages/ProjectSettings';
import Feedback from './pages/Feedback';
import CardTiming, { ApplyAutoFitResult } from './pages/CardTiming';
import GoogleSheetView from './pages/GoogleSheetView';
import { CardTimingValues, calculateAutoFitTarget } from './utils/cardTiming';
import AdminBackOffice from './pages/AdminBackOffice';
import { AppStatsigProvider, trackEvent, trackPageView } from './statsig';
import { DEFAULT_COLUMNS, extractDataFromRows } from './loaders/shared';
import { PeriodicProjectTableSaveQueue, type ProjectSaveStatus } from './utils/periodicProjectTableSaveQueue';
import { isInaccessibleUserAssetUrl } from './utils/assetOwnership';
import { SkeletonEditor, SkeletonPage, SkeletonTable } from './components/Skeleton';
import {
  clampEditPreviewPaneHeight,
  clampEditPreviewPaneWidth,
  DEFAULT_EDIT_PREVIEW_PANE_RATIO,
  EDIT_PANE_MIN_HEIGHT,
  EDIT_HORIZONTAL_LAYOUT_MIN_WIDTH,
  EDIT_HORIZONTAL_PREVIEW_MIN_WIDTH,
  getStoredEditorMode,
  storeEditorMode,
  type EditorMode,
} from './utils/editorLayout';

export interface RowData {
  [key: string]: string | number;
}

interface SelectedLogoGlow {
  blockId: string;
  logoRowIndex: number;
}

interface ProjectTableSaveSnapshot {
  blocks: Block[];
  videoSettings: VideoSettingsType;
  cardGroups: CardGroup[];
  defaultCardSettings?: DefaultCardSettings;
  columnDefs: any[];
  originalSheetName: string;
  projectId: string;
  rowDataLength: number;
}

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

interface DefaultFontFamilyDefinition {
  familyName: string;
  folderPath: string;
  members: string[];
}

const buildSourceSerifMembers = (): string[] => {
  const prefixes: string[] = [
    'SourceSerif4',
    'SourceSerif4Caption',
    'SourceSerif4Display',
    'SourceSerif4SmText',
    'SourceSerif4Subhead',
  ];
  const styles: string[] = [
    'Black',
    'BlackIt',
    'Bold',
    'BoldIt',
    'ExtraLight',
    'ExtraLightIt',
    'It',
    'Light',
    'LightIt',
    'Regular',
    'Semibold',
    'SemiboldIt',
  ];
  const staticMembers: string[] = prefixes.flatMap((prefix: string) =>
    styles.flatMap((style: string) => {
      const name: string = `${prefix}-${style}`;
      return [name, `${name}.otf`, `${name}.ttf`];
    })
  );
  const variableMembers: string[] = ['SourceSerif4Variable-Italic', 'SourceSerif4Variable-Roman']
    .flatMap((name: string) => [name, `${name}.otf`, `${name}.ttf`]);
  return [...staticMembers, ...variableMembers];
};

const DEFAULT_FONT_DEFINITIONS: DefaultFontFamilyDefinition[] = [
  { familyName: 'rubik', folderPath: 'ofl-rubik', members: ['Rubik[wght]', 'Rubik-Italic[wght]'] },
  { familyName: 'frankruhllibre', folderPath: 'ofl-frankruhllibre', members: ['FrankRuhlLibre[wght]'] },
  { familyName: 'assistant', folderPath: 'ofl-assistant', members: ['Assistant[wght]'] },
  { familyName: 'heebo', folderPath: 'ofl-heebo', members: ['Heebo[wght]'] },
  { familyName: 'notosanshebrew', folderPath: 'ofl-notosanshebrew', members: ['NotoSansHebrew[wdth,wght]'] },
  { familyName: 'notoserifhebrew', folderPath: 'ofl-notoserifhebrew', members: ['NotoSerifHebrew[wdth,wght]'] },
  { familyName: 'miriamlibre', folderPath: 'ofl-miriamlibre', members: ['MiriamLibre[wght]'] },
  { familyName: 'alef', folderPath: 'ofl-alef', members: ['Alef-Bold', 'Alef-Regular'] },
  { familyName: 'davidlibre', folderPath: 'ofl-davidlibre', members: ['DavidLibre-Bold', 'DavidLibre-Medium', 'DavidLibre-Regular'] },
  { familyName: 'oswald', folderPath: 'ofl-oswald', members: ['Oswald[wght]'] },
  { familyName: 'robotocondensed', folderPath: 'ofl-robotocondensed', members: ['RobotoCondensed[wght]', 'RobotoCondensed-Italic[wght]'] },
  { familyName: 'apache-open-sans-hebrew-condensed', folderPath: 'apache-open-sans-hebrew-condensed', members: ['OpenSansHebrewCondensed-Bold', 'OpenSansHebrewCondensed-BoldItalic', 'OpenSansHebrewCondensed-ExtraBold', 'OpenSansHebrewCondensed-ExtraBoldItalic', 'OpenSansHebrewCondensed-Italic', 'OpenSansHebrewCondensed-Light', 'OpenSansHebrewCondensed-LightItalic', 'OpenSansHebrewCondensed-Regular'] },
  { familyName: 'archivonarrow', folderPath: 'ofl-archivonarrow', members: ['ArchivoNarrow[wght]', 'ArchivoNarrow-Italic[wght]'] },
  { familyName: 'raleway', folderPath: 'ofl-raleway', members: ['Raleway[wght]', 'Raleway-Italic[wght]'] },
  { familyName: 'sourcesans3', folderPath: 'ofl-sourcesans3', members: ['SourceSans3[wght]', 'SourceSans3-Italic[wght]'] },
  { familyName: 'sairacondensed', folderPath: 'ofl-sairacondensed', members: ['SairaCondensed-Black', 'SairaCondensed-Bold', 'SairaCondensed-ExtraBold', 'SairaCondensed-ExtraLight', 'SairaCondensed-Light', 'SairaCondensed-Medium', 'SairaCondensed-Regular', 'SairaCondensed-SemiBold', 'SairaCondensed-Thin'] },
  { familyName: 'titilliumweb', folderPath: 'ofl-titilliumweb', members: ['TitilliumWeb-Black', 'TitilliumWeb-Bold', 'TitilliumWeb-BoldItalic', 'TitilliumWeb-ExtraLight', 'TitilliumWeb-ExtraLightItalic', 'TitilliumWeb-Italic', 'TitilliumWeb-Light', 'TitilliumWeb-LightItalic', 'TitilliumWeb-Regular', 'TitilliumWeb-SemiBold', 'TitilliumWeb-SemiBoldItalic'] },
  { familyName: 'barlowcondensed', folderPath: 'ofl-barlowcondensed', members: ['BarlowCondensed-Black', 'BarlowCondensed-BlackItalic', 'BarlowCondensed-Bold', 'BarlowCondensed-BoldItalic', 'BarlowCondensed-ExtraBold', 'BarlowCondensed-ExtraBoldItalic', 'BarlowCondensed-ExtraLight', 'BarlowCondensed-ExtraLightItalic', 'BarlowCondensed-Italic', 'BarlowCondensed-Light', 'BarlowCondensed-LightItalic', 'BarlowCondensed-Medium', 'BarlowCondensed-MediumItalic', 'BarlowCondensed-Regular', 'BarlowCondensed-SemiBold', 'BarlowCondensed-SemiBoldItalic', 'BarlowCondensed-Thin', 'BarlowCondensed-ThinItalic'] },
  { familyName: 'notosans', folderPath: 'ofl-notosans', members: ['NotoSans[wdth,wght]', 'NotoSans-Italic[wdth,wght]'] },
  { familyName: 'opensans', folderPath: 'ofl-opensans', members: ['OpenSans[wdth,wght]', 'OpenSans-Italic[wdth,wght]'] },
  { familyName: 'lato', folderPath: 'ofl-lato', members: ['Lato-Black', 'Lato-BlackItalic', 'Lato-Bold', 'Lato-BoldItalic', 'Lato-ExtraBold', 'Lato-ExtraBoldItalic', 'Lato-ExtraLight', 'Lato-ExtraLightItalic', 'Lato-Italic', 'Lato-Light', 'Lato-LightItalic', 'Lato-Medium', 'Lato-MediumItalic', 'Lato-Regular', 'Lato-SemiBold', 'Lato-SemiBoldItalic', 'Lato-Thin', 'Lato-ThinItalic'] },
  { familyName: 'jetbrainsmono', folderPath: 'ofl-jetbrainsmono', members: ['JetBrainsMono[wght]', 'JetBrainsMono-Italic[wght]'] },
  { familyName: 'apache-tinos', folderPath: 'apache-tinos', members: ['Tinos-Bold', 'Tinos-BoldItalic', 'Tinos-Italic', 'Tinos-Regular'] },
  { familyName: 'roboto', folderPath: 'ofl-roboto', members: ['Roboto[wdth,wght]', 'Roboto-Italic[wdth,wght]'] },
  { familyName: 'archivo', folderPath: 'ofl-archivo', members: ['Archivo[wdth,wght]', 'Archivo-Italic[wdth,wght]'] },
  { familyName: 'montserrat', folderPath: 'ofl-montserrat', members: ['Montserrat[wght]', 'Montserrat-Italic[wght]'] },
  { familyName: 'apache-arimo', folderPath: 'apache-arimo', members: ['Arimo[wght]', 'Arimo-Italic[wght]'] },
  { familyName: 'notosansmono', folderPath: 'ofl-notosansmono', members: ['NotoSansMono[wdth,wght]'] },
  { familyName: 'manrope', folderPath: 'ofl-manrope', members: ['Manrope[wght]'] },
  { familyName: 'inter', folderPath: 'ofl-inter', members: ['Inter[opsz,wght]', 'Inter-Italic[opsz,wght]'] },
  { familyName: 'firamono', folderPath: 'ofl-firamono', members: ['FiraMono-Bold', 'FiraMono-Medium', 'FiraMono-Regular'] },
  { familyName: 'publicsans', folderPath: 'ofl-publicsans', members: ['PublicSans[wght]', 'PublicSans-Italic[wght]'] },
  { familyName: 'suezone', folderPath: 'ofl-suezone', members: ['SuezOne-Regular'] },
  { familyName: 'varelaround', folderPath: 'ofl-varelaround', members: ['VarelaRound-Regular'] },
  { familyName: 'apache-cousine', folderPath: 'apache-cousine', members: ['Cousine-Bold', 'Cousine-BoldItalic', 'Cousine-Italic', 'Cousine-Regular'] },
  { familyName: 'spacegrotesk', folderPath: 'ofl-spacegrotesk', members: ['SpaceGrotesk[wght]'] },
  { familyName: 'worksans', folderPath: 'ofl-worksans', members: ['WorkSans[wght]', 'WorkSans-Italic[wght]'] },
  { familyName: 'librefranklin', folderPath: 'ofl-librefranklin', members: ['LibreFranklin[wght]', 'LibreFranklin-Italic[wght]'] },
  { familyName: 'secularone', folderPath: 'ofl-secularone', members: ['SecularOne-Regular'] },
  { familyName: 'firasans', folderPath: 'ofl-firasans', members: ['FiraSans-Black', 'FiraSans-BlackItalic', 'FiraSans-Bold', 'FiraSans-BoldItalic', 'FiraSans-ExtraBold', 'FiraSans-ExtraBoldItalic', 'FiraSans-ExtraLight', 'FiraSans-ExtraLightItalic', 'FiraSans-Italic', 'FiraSans-Light', 'FiraSans-LightItalic', 'FiraSans-Medium', 'FiraSans-MediumItalic', 'FiraSans-Regular', 'FiraSans-SemiBold', 'FiraSans-SemiBoldItalic', 'FiraSans-Thin', 'FiraSans-ThinItalic'] },
  { familyName: 'plex-sans-variable', folderPath: 'plex-sans-variable', members: ['IBM Plex Sans Var-Italic', 'IBM Plex Sans Var-Italic-Cyrillic', 'IBM Plex Sans Var-Italic-Greek', 'IBM Plex Sans Var-Italic-Latin1', 'IBM Plex Sans Var-Italic-Latin2', 'IBM Plex Sans Var-Italic-Latin3', 'IBM Plex Sans Var-Italic-Pi', 'IBM Plex Sans Var-Roman', 'IBM Plex Sans Var-Roman-Cyrillic', 'IBM Plex Sans Var-Roman-Greek', 'IBM Plex Sans Var-Roman-Latin1', 'IBM Plex Sans Var-Roman-Latin2', 'IBM Plex Sans Var-Roman-Latin3', 'IBM Plex Sans Var-Roman-Pi'] },
  { familyName: 'plex-serif-variable', folderPath: 'plex-serif-variable', members: ['IBM Plex Serif Var-Italic', 'IBM Plex Serif Var-Italic-Cyrillic', 'IBM Plex Serif Var-Italic-Greek', 'IBM Plex Serif Var-Italic-Latin1', 'IBM Plex Serif Var-Italic-Latin2', 'IBM Plex Serif Var-Italic-Latin3', 'IBM Plex Serif Var-Italic-Pi', 'IBM Plex Serif Var-Roman', 'IBM Plex Serif Var-Roman-Cyrillic', 'IBM Plex Serif Var-Roman-Greek', 'IBM Plex Serif Var-Roman-Latin1', 'IBM Plex Serif Var-Roman-Latin2', 'IBM Plex Serif Var-Roman-Latin3', 'IBM Plex Serif Var-Roman-Pi'] },
  { familyName: 'plex-mono', folderPath: 'plex-mono', members: ['IBMPlexMono-Bold', 'IBMPlexMono-Bold-Cyrillic', 'IBMPlexMono-BoldItalic', 'IBMPlexMono-BoldItalic-Cyrillic', 'IBMPlexMono-BoldItalic-Latin1', 'IBMPlexMono-BoldItalic-Latin2', 'IBMPlexMono-BoldItalic-Latin3', 'IBMPlexMono-BoldItalic-Pi', 'IBMPlexMono-Bold-Latin1', 'IBMPlexMono-Bold-Latin2', 'IBMPlexMono-Bold-Latin3', 'IBMPlexMono-Bold-Pi', 'IBMPlexMono-ExtraLight', 'IBMPlexMono-ExtraLight-Cyrillic', 'IBMPlexMono-ExtraLightItalic', 'IBMPlexMono-ExtraLightItalic-Cyrillic', 'IBMPlexMono-ExtraLightItalic-Latin1', 'IBMPlexMono-ExtraLightItalic-Latin2', 'IBMPlexMono-ExtraLightItalic-Latin3', 'IBMPlexMono-ExtraLightItalic-Pi', 'IBMPlexMono-ExtraLight-Latin1', 'IBMPlexMono-ExtraLight-Latin2', 'IBMPlexMono-ExtraLight-Latin3', 'IBMPlexMono-ExtraLight-Pi', 'IBMPlexMono-Italic', 'IBMPlexMono-Italic-Cyrillic', 'IBMPlexMono-Italic-Latin1', 'IBMPlexMono-Italic-Latin2', 'IBMPlexMono-Italic-Latin3', 'IBMPlexMono-Italic-Pi', 'IBMPlexMono-Light', 'IBMPlexMono-Light-Cyrillic', 'IBMPlexMono-LightItalic', 'IBMPlexMono-LightItalic-Cyrillic', 'IBMPlexMono-LightItalic-Latin1', 'IBMPlexMono-LightItalic-Latin2', 'IBMPlexMono-LightItalic-Latin3', 'IBMPlexMono-LightItalic-Pi', 'IBMPlexMono-Light-Latin1', 'IBMPlexMono-Light-Latin2', 'IBMPlexMono-Light-Latin3', 'IBMPlexMono-Light-Pi', 'IBMPlexMono-Medium', 'IBMPlexMono-Medium-Cyrillic', 'IBMPlexMono-MediumItalic', 'IBMPlexMono-MediumItalic-Cyrillic', 'IBMPlexMono-MediumItalic-Latin1', 'IBMPlexMono-MediumItalic-Latin2', 'IBMPlexMono-MediumItalic-Latin3', 'IBMPlexMono-MediumItalic-Pi', 'IBMPlexMono-Medium-Latin1', 'IBMPlexMono-Medium-Latin2', 'IBMPlexMono-Medium-Latin3', 'IBMPlexMono-Medium-Pi', 'IBMPlexMono-Regular', 'IBMPlexMono-Regular-Cyrillic', 'IBMPlexMono-Regular-Latin1', 'IBMPlexMono-Regular-Latin2', 'IBMPlexMono-Regular-Latin3', 'IBMPlexMono-Regular-Pi', 'IBMPlexMono-SemiBold', 'IBMPlexMono-SemiBold-Cyrillic', 'IBMPlexMono-SemiBoldItalic', 'IBMPlexMono-SemiBoldItalic-Cyrillic', 'IBMPlexMono-SemiBoldItalic-Latin1', 'IBMPlexMono-SemiBoldItalic-Latin2', 'IBMPlexMono-SemiBoldItalic-Latin3', 'IBMPlexMono-SemiBoldItalic-Pi', 'IBMPlexMono-SemiBold-Latin1', 'IBMPlexMono-SemiBold-Latin2', 'IBMPlexMono-SemiBold-Latin3', 'IBMPlexMono-SemiBold-Pi', 'IBMPlexMono-Text', 'IBMPlexMono-Text-Cyrillic', 'IBMPlexMono-TextItalic', 'IBMPlexMono-TextItalic-Cyrillic', 'IBMPlexMono-TextItalic-Latin1', 'IBMPlexMono-TextItalic-Latin2', 'IBMPlexMono-TextItalic-Latin3', 'IBMPlexMono-TextItalic-Pi', 'IBMPlexMono-Text-Latin1', 'IBMPlexMono-Text-Latin2', 'IBMPlexMono-Text-Latin3', 'IBMPlexMono-Text-Pi', 'IBMPlexMono-Thin', 'IBMPlexMono-Thin-Cyrillic', 'IBMPlexMono-ThinItalic', 'IBMPlexMono-ThinItalic-Cyrillic', 'IBMPlexMono-ThinItalic-Latin1', 'IBMPlexMono-ThinItalic-Latin2', 'IBMPlexMono-ThinItalic-Latin3', 'IBMPlexMono-ThinItalic-Pi', 'IBMPlexMono-Thin-Latin1', 'IBMPlexMono-Thin-Latin2', 'IBMPlexMono-Thin-Latin3', 'IBMPlexMono-Thin-Pi'] },
  { familyName: 'plex-sans-hebrew', folderPath: 'plex-sans-hebrew', members: ['IBMPlexSansHebrew-Bold', 'IBMPlexSansHebrew-ExtraLight', 'IBMPlexSansHebrew-Light', 'IBMPlexSansHebrew-Medium', 'IBMPlexSansHebrew-Regular', 'IBMPlexSansHebrew-SemiBold', 'IBMPlexSansHebrew-Text', 'IBMPlexSansHebrew-Thin'] },
  { familyName: 'Cairo', folderPath: 'Cairo', members: ['Cairo-VariableFont_slnt,wght'] },
  { familyName: 'clear-sans-main', folderPath: 'clear-sans-main', members: ['ClearSans-Bold', 'ClearSans-BoldItalic', 'ClearSans-Italic', 'ClearSans-Light', 'ClearSans-Medium', 'ClearSans-MediumItalic', 'ClearSans-Regular', 'ClearSans-Thin'] },
  { familyName: 'source-serif-release', folderPath: 'source-serif-release', members: buildSourceSerifMembers() },
];

const toPublicFontUrl = (fontPath: string): string =>
  `/fonts/${fontPath.split('/').map((segment: string) => encodeURIComponent(segment)).join('/')}`;

const isSplitPlexFont = (fontName: string): boolean =>
  /-(Cyrillic|Greek|Latin[123]|Pi)$/.test(fontName);

const resolveDefaultFontPath = (folderPath: string, fontName: string): string => {
  const folder: string = folderPath.replace(/^FinTitles-Fonts[\\/]/, '').replace(/\\/g, '/');
  if (folder === 'clear-sans-main') {
    return `${folder}/TTF/${fontName}.ttf`;
  }
  if (folder === 'plex-sans-hebrew') {
    return `${folder}/fonts/complete/woff2/${fontName}.woff2`;
  }
  if (folder === 'plex-sans-variable' || folder === 'plex-serif-variable' || folder === 'plex-mono') {
    const plexSet: string = isSplitPlexFont(fontName) ? 'split' : 'complete';
    return `${folder}/fonts/${plexSet}/woff2/${fontName}.woff2`;
  }
  if (folder === 'source-serif-release') {
    const sourceFolder: string = fontName.startsWith('SourceSerif4Variable')
      ? 'VAR'
      : fontName.endsWith('.otf')
        ? 'OTF'
        : 'TTF';
    const fileName: string = fontName.endsWith('.otf') || fontName.endsWith('.ttf')
      ? fontName
      : `${fontName}.ttf`;
    return `${folder}/${sourceFolder}/${fileName}`;
  }
  return `${folder}/${fontName}.ttf`;
};

const hasVariableFontAxes = (fontName: string): boolean =>
  fontName.includes('[') || fontName.includes('Variable') || fontName.includes(' Var-');

const defaultFonts: FontFamilyInfo[] = DEFAULT_FONT_DEFINITIONS.map((definition: DefaultFontFamilyDefinition) => ({
  familyName: definition.familyName,
  fonts: definition.members.map((fontName: string) => {
    const fontPath: string = resolveDefaultFontPath(definition.folderPath, fontName);
    const fileName: string = fontPath.split('/').pop() ?? `${fontName}.ttf`;
    return {
      name: fontName,
      familyName: definition.familyName,
      fileName,
      file: null,
      url: toPublicFontUrl(fontPath),
      enabled: true,
      source: 'default',
      variableFontConfig: hasVariableFontAxes(fontName)
        ? getFallbackVariableFontConfig(fontName)
        : undefined,
    };
  }),
}));

const cloneFontFamilies = (fontFamilies: FontFamilyInfo[]): FontFamilyInfo[] =>
  fontFamilies.map((fontFamily: FontFamilyInfo) => ({
    familyName: fontFamily.familyName,
    fonts: fontFamily.fonts.map((font: FontInfo) => ({ ...font })),
  }));

const flattenFontFamilies = (fontFamilies: FontFamilyInfo[]): FontInfo[] =>
  fontFamilies.flatMap((fontFamily: FontFamilyInfo) => fontFamily.fonts);

const defaultFontNameSet: Set<string> = new Set(flattenFontFamilies(defaultFonts).map((font: FontInfo) => font.name));

const mergeFontSettingsWithDefaultFamilies = (
  fontsFromSettings: FontSettingsMap | undefined,
  accessibleProjectIds: Set<string> | undefined
): FontFamilyInfo[] => {
  const nextFontFamilies: FontFamilyInfo[] = cloneFontFamilies(defaultFonts);
  const userFonts: FontInfo[] = Object.entries(fontsFromSettings ?? {})
    .filter(([fontName]) => !defaultFontNameSet.has(fontName))
    .map(([fontName, fontEntry]) => {
      const typedFontEntry: FontSettingsEntry = fontEntry as FontSettingsEntry;
      const url: string = getFontEntryUrl(typedFontEntry);
      return {
        name: fontName,
        familyName: USER_UPLOADED_FONT_FAMILY,
        fileName: decodeURIComponent(url.split('/').pop() ?? fontName),
        file: null,
        url,
        enabled: getFontEntryEnabled(typedFontEntry),
        source: 'user' as const,
        variableFontConfig: getFontEntryVariableConfig(typedFontEntry),
      };
    })
    .filter((font: FontInfo) => Boolean(font.url) && !isInaccessibleUserAssetUrl(font.url, accessibleProjectIds, 'fonts'));

  if (userFonts.length > 0) {
    nextFontFamilies.push({ familyName: USER_UPLOADED_FONT_FAMILY, fonts: userFonts });
  }
  return nextFontFamilies;
};

const filterProjectLogos = (
  logosFromSettings: unknown,
  accessibleProjectIds: Set<string> | undefined
): Record<string, { url: string; isEnabled: boolean }> => {
  if (!logosFromSettings || typeof logosFromSettings !== 'object') {
    return {};
  }

  const filteredLogos: Record<string, { url: string; isEnabled: boolean }> = {};
  for (const [name, logoData] of Object.entries(logosFromSettings as Record<string, unknown>)) {
    let url: string = '';
    let isEnabled: boolean = true;
    if (typeof logoData === 'string') {
      url = logoData;
    } else if (logoData && typeof logoData === 'object') {
      const typedLogoData = logoData as { url?: unknown; isEnabled?: unknown };
      url = typeof typedLogoData.url === 'string' ? typedLogoData.url : '';
      isEnabled = typedLogoData.isEnabled !== undefined ? Boolean(typedLogoData.isEnabled) : true;
    }
    if (url && !isInaccessibleUserAssetUrl(url, accessibleProjectIds, 'logos')) {
      filteredLogos[name] = { url, isEnabled };
    }
  }
  return filteredLogos;
};

const toFontFamilyOptions = (fontFamilies: FontFamilyInfo[]): FontFamilyOption[] =>
  fontFamilies
    .map((fontFamily: FontFamilyInfo) => ({
      familyName: fontFamily.familyName,
      fonts: fontFamily.fonts
        .filter((font: FontInfo) => font.enabled)
        .map((font: FontInfo) => ({
          value: font.name,
          label: font.name,
          familyName: fontFamily.familyName,
          variableFontConfig: font.variableFontConfig,
        })),
    }))
    .filter((fontFamily: FontFamilyOption) => fontFamily.fonts.length > 0);

const fontFamiliesToFontSettingsMap = (fontFamilies: FontFamilyInfo[]): FontSettingsMap => {
  const fontSettings: FontSettingsMap = {};
  for (const font of flattenFontFamilies(fontFamilies)) {
    if (font.url) {
      fontSettings[font.name] = {
        url: font.url,
        variableFontConfig: font.variableFontConfig,
      };
    }
  }
  return fontSettings;
};

const variableFontConfigForStorage = (
  variableFontConfig: VariableFontConfig | undefined
): Omit<VariableFontConfig, 'family'> | undefined => {
  if (!variableFontConfig) {
    return undefined;
  }
  const { family: _family, ...configForStorage } = variableFontConfig;
  return configForStorage;
};

const uploadedFontFamiliesToFontSettingsMap = (fontFamilies: FontFamilyInfo[]): FontSettingsMap => {
  const fontSettings: FontSettingsMap = {};
  for (const font of flattenFontFamilies(fontFamilies)) {
    if (font.source !== 'user' || !font.url) {
      continue;
    }
    fontSettings[font.name] = {
      url: font.url,
      variableFontConfig: variableFontConfigForStorage(font.variableFontConfig),
      isEnabled: font.enabled,
    };
  }
  return fontSettings;
};

const fontMetadataForStorage = (fontMetadata: FontMetadata): FontMetadata => ({
  url: fontMetadata.url,
  variableFontConfig: variableFontConfigForStorage(fontMetadata.variableFontConfig),
  isEnabled: fontMetadata.isEnabled,
});

const withRenderableFonts = (
  videoSettings: VideoSettingsType,
  fontFamilies: FontFamilyInfo[]
): VideoSettingsType => ({
  ...videoSettings,
  fonts: {
    ...fontFamiliesToFontSettingsMap(fontFamilies),
    ...(videoSettings.fonts ?? {}),
  },
});

const buildFontFaceStyles = (fontFamilies: FontFamilyInfo[]): string =>
  flattenFontFamilies(fontFamilies)
    .filter((font: FontInfo) => Boolean(font.url))
    .map((font: FontInfo) => (
      `@font-face { font-family: ${JSON.stringify(font.name)}; src: url(${JSON.stringify(font.url)}); font-weight: normal; font-style: normal; }`
    ))
    .join('\n');

function MainAppContent({
  rowData,
  columnDefs,
  originalSheetName,
  firstColumnValues,
  blocks,
  selectedBlockIndex,
  videoSettings,
  defaultCardSettings,
  fonts,
  logos,
  googleSheetsUrl,
  isGoogleSheetsLoading,
  googleSheetsError,
  saveStatus,
  onRetrySave,
  isBlockPropertiesCollapsed,
  handleSaveBlocks,
  handleRowDataChange,
  handleVideoSettingsChange,
  setSelectedBlockIndex,
  scrollToBlockCounter,
  setScrollToBlockCounter,
  setIsBlockPropertiesCollapsed,
  onLoadGoogleSheets,
  cardGroups,
  onSplitCardAt,
  onMergeCardWithPrevious,
  readOnly,
  projectSettingsReadOnly,
  editorMode,
  onEditorModeChange,
  projectId,
  renderBackupAction,
}: any) {
  const { i18n: linguiI18n } = useLingui();
  const isRtlLayout: boolean = linguiI18n.locale === 'he';
  const editWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const [editWorkspaceNode, setEditWorkspaceNode] = useState<HTMLDivElement | null>(null);
  const removeEditDividerPointerListenersRef = useRef<(() => void) | null>(null);
  const [editPreviewPaneHeight, setEditPreviewPaneHeight] = useState<number | null>(null);
  const [editPreviewPaneWidth, setEditPreviewPaneWidth] = useState<number | null>(null);
  const [editWorkspaceHeight, setEditWorkspaceHeight] = useState<number>(0);
  const [editWorkspaceWidth, setEditWorkspaceWidth] = useState<number>(0);

  const selectedBlock: Block | undefined =
    selectedBlockIndex !== null && selectedBlockIndex >= 0 && selectedBlockIndex < blocks.length
      ? blocks[selectedBlockIndex]
      : undefined;
  const [selectedLogoGlow, setSelectedLogoGlow] = useState<SelectedLogoGlow | null>(null);
  const selectedLogoRowIndex: number | null =
    selectedBlock?.isLogoBlock && selectedLogoGlow?.blockId === selectedBlock.id
      ? selectedLogoGlow.logoRowIndex
      : null;
  const filteredRowData = selectedBlock?.dataRows || [];
  const fontFamilyOptions: FontFamilyOption[] = useMemo(
    () => toFontFamilyOptions(fonts),
    [fonts]
  );
  const renderVideoSettings: VideoSettingsType = useMemo(
    () => withRenderableFonts(videoSettings, fonts),
    [fonts, videoSettings]
  );

  const updateEditPreviewPaneHeight = useCallback((requestedHeight: number | null) => {
    const workspaceHeight = editWorkspaceRef.current?.clientHeight ?? 0;
    setEditPreviewPaneHeight(clampEditPreviewPaneHeight(requestedHeight, workspaceHeight));
  }, []);

  const updateEditPreviewPaneWidth = useCallback((requestedWidth: number | null) => {
    const workspaceWidth = editWorkspaceRef.current?.clientWidth ?? 0;
    setEditPreviewPaneWidth(clampEditPreviewPaneWidth(requestedWidth, workspaceWidth));
  }, []);

  const syncEditWorkspaceSize = useCallback((workspace: HTMLDivElement | null): void => {
    if (!workspace) return;

    const workspaceHeight = workspace.clientHeight;
    const workspaceWidth = workspace.clientWidth;
    setEditWorkspaceHeight(workspaceHeight);
    setEditWorkspaceWidth(workspaceWidth);
    setEditPreviewPaneHeight((currentHeight: number | null) => (
      currentHeight === null ? null : clampEditPreviewPaneHeight(currentHeight, workspaceHeight)
    ));
    setEditPreviewPaneWidth((currentWidth: number | null) => (
      currentWidth === null ? null : clampEditPreviewPaneWidth(currentWidth, workspaceWidth)
    ));
  }, []);

  const setEditWorkspaceElement = useCallback((workspace: HTMLDivElement | null): void => {
    editWorkspaceRef.current = workspace;
    setEditWorkspaceNode(workspace);
    syncEditWorkspaceSize(workspace);
  }, [syncEditWorkspaceSize]);

  useEffect(() => {
    setEditPreviewPaneHeight(null);
    setEditPreviewPaneWidth(null);
  }, [projectId]);

  useEffect(() => {
    if (editorMode !== 'edit') return;

    const workspace = editWorkspaceNode;
    if (!workspace) return;

    const updateWorkspaceSize = (): void => syncEditWorkspaceSize(workspace);

    updateWorkspaceSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWorkspaceSize);
      return () => window.removeEventListener('resize', updateWorkspaceSize);
    }

    const resizeObserver = new ResizeObserver(updateWorkspaceSize);
    resizeObserver.observe(workspace);
    return () => {
      resizeObserver.disconnect();
    };
  }, [editWorkspaceNode, editorMode, syncEditWorkspaceSize]);

  useEffect(() => {
    return () => removeEditDividerPointerListenersRef.current?.();
  }, [editorMode]);

  const isHorizontalEditLayout = editWorkspaceWidth >= EDIT_HORIZONTAL_LAYOUT_MIN_WIDTH;

  const handleEditDividerPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const workspace = editWorkspaceRef.current;
    if (!workspace) return;

    event.preventDefault();
    removeEditDividerPointerListenersRef.current?.();
    const divider: HTMLDivElement = event.currentTarget;
    const pointerId: number = event.pointerId;
    divider.setPointerCapture(pointerId);
    const workspaceRect = workspace.getBoundingClientRect();

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      if (moveEvent.pointerId !== pointerId) return;
      if (isHorizontalEditLayout) {
        // CSS Grid lays out the preview on the inline end. In RTL that is the
        // left edge, so measure from the matching workspace edge.
        updateEditPreviewPaneWidth(
          isRtlLayout
            ? moveEvent.clientX - workspaceRect.left
            : workspaceRect.right - moveEvent.clientX
        );
      } else {
        updateEditPreviewPaneHeight(workspaceRect.bottom - moveEvent.clientY);
      }
    };
    const removePointerListeners = (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      divider.removeEventListener('lostpointercapture', handleLostPointerCapture);
      if (divider.hasPointerCapture(pointerId)) {
        divider.releasePointerCapture(pointerId);
      }
      if (removeEditDividerPointerListenersRef.current === removePointerListeners) {
        removeEditDividerPointerListenersRef.current = null;
      }
    };
    const handlePointerUp = (upEvent: PointerEvent): void => {
      if (upEvent.pointerId !== pointerId) return;
      removePointerListeners();
    };
    const handleLostPointerCapture = (): void => removePointerListeners();

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    divider.addEventListener('lostpointercapture', handleLostPointerCapture);
    removeEditDividerPointerListenersRef.current = removePointerListeners;
  }, [isHorizontalEditLayout, isRtlLayout, updateEditPreviewPaneHeight, updateEditPreviewPaneWidth]);

  const handleEditDividerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isHorizontalEditLayout) {
      const workspaceWidth = editWorkspaceRef.current?.clientWidth ?? 0;
      const currentWidth = clampEditPreviewPaneWidth(editPreviewPaneWidth, workspaceWidth);
      const maximumWidth = clampEditPreviewPaneWidth(Number.MAX_SAFE_INTEGER, workspaceWidth);

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        updateEditPreviewPaneWidth(currentWidth + 40);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        updateEditPreviewPaneWidth(currentWidth - 40);
      } else if (event.key === 'Home') {
        event.preventDefault();
        updateEditPreviewPaneWidth(EDIT_HORIZONTAL_PREVIEW_MIN_WIDTH);
      } else if (event.key === 'End') {
        event.preventDefault();
        updateEditPreviewPaneWidth(maximumWidth);
      }
      return;
    }

    const workspaceHeight = editWorkspaceRef.current?.clientHeight ?? 0;
    const currentHeight = clampEditPreviewPaneHeight(editPreviewPaneHeight, workspaceHeight);
    const maximumHeight = clampEditPreviewPaneHeight(Number.MAX_SAFE_INTEGER, workspaceHeight);

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      updateEditPreviewPaneHeight(currentHeight + 40);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      updateEditPreviewPaneHeight(currentHeight - 40);
    } else if (event.key === 'Home') {
      event.preventDefault();
      updateEditPreviewPaneHeight(EDIT_PANE_MIN_HEIGHT);
    } else if (event.key === 'End') {
      event.preventDefault();
      updateEditPreviewPaneHeight(maximumHeight);
    }
  }, [editPreviewPaneHeight, editPreviewPaneWidth, isHorizontalEditLayout, updateEditPreviewPaneHeight, updateEditPreviewPaneWidth]);

  const editPreviewPaneValue = clampEditPreviewPaneHeight(editPreviewPaneHeight, editWorkspaceHeight);
  const editPreviewPaneMaximum = clampEditPreviewPaneHeight(Number.MAX_SAFE_INTEGER, editWorkspaceHeight);
  const editPreviewPaneWidthValue = clampEditPreviewPaneWidth(editPreviewPaneWidth, editWorkspaceWidth);
  const editPreviewPaneWidthMaximum = clampEditPreviewPaneWidth(Number.MAX_SAFE_INTEGER, editWorkspaceWidth);

  const hasSelectedBlockData = Boolean(selectedBlock && columnDefs.length > 0 && filteredRowData.length > 0);
  const emptyEditorMessage = selectedBlock
    ? t`No data associated with the selected block.`
    : t`Please select a block to view associated data.`;

  const handlePreviewBlockChange = async (blockIndex: number, updatedBlock: Block): Promise<void> => {
    const updatedBlocks: Block[] = [...blocks];
    updatedBlocks[blockIndex] = updatedBlock;
    await handleSaveBlocks(updatedBlocks);
  };

  const previewContent = selectedBlock && (
    <HtmlPreview
      blocks={blocks}
      actualSelectedBlockIndex={selectedBlockIndex!}
      videoSettings={renderVideoSettings}
      columnDefs={columnDefs}
      scrollToBlockCounter={scrollToBlockCounter}
      cardGroups={cardGroups}
      readOnly={readOnly}
      syncFocusToVisibleViewport={editorMode === 'edit'}
      selectedLogoRowIndex={selectedLogoRowIndex}
      onPreviewFocusedBlock={setSelectedBlockIndex}
    />
  );

  const dataGridContent = selectedBlock && (
    <DataGrid
      rowData={filteredRowData}
      columnDefs={columnDefs}
      isLogoBlock={selectedBlock.isLogoBlock}
      isSingleColumn={selectedBlock.isSingleCell}
      isSongBlock={selectedBlock.isSongBlock}
      blockId={selectedBlock.id}
      blockName={selectedBlock.name}
      numColumns={selectedBlock.numColumns}
      labelValueAlignment={selectedBlock.labelValueAlignment}
      ltrRtlControl={selectedBlock.ltrRtlControl}
      logoOptions={logos.filter((logo: LogoInfo) => logo.enabled).map((logo: LogoInfo) => logo.name)}
      onRowDataChange={handleRowDataChange}
      readOnly={readOnly}
    />
  );

  const logoConfigContent = selectedBlock?.isLogoBlock && !readOnly && selectedBlockIndex !== null && (
    <LogoBlockConfig
      block={selectedBlock}
      blockIndex={selectedBlockIndex}
      onBlockChange={(updatedBlock: Block) => handlePreviewBlockChange(selectedBlockIndex, updatedBlock)}
      onLogoRowSelect={(logoRowIndex: number) => {
        if (!selectedBlock) return;
        setSelectedLogoGlow({ blockId: selectedBlock.id, logoRowIndex });
      }}
    />
  );

  return (
    <div className="App">
      <NavBar
        editorMode={editorMode}
        onEditorModeChange={onEditorModeChange}
        showCardTiming={videoSettings.mode === 'cards'}
        canEdit={!projectSettingsReadOnly}
        googleSheetsUrl={googleSheetsUrl}
        onLoadGoogleSheets={onLoadGoogleSheets}
        isGoogleSheetsLoading={isGoogleSheetsLoading}
        renderBackupAction={renderBackupAction}
        saveStatus={saveStatus}
        onRetrySave={onRetrySave}
      />
      <main className="App-content">
        {googleSheetsError && <div className="workspace-load-alert" role="alert">{googleSheetsError}</div>}

        {rowData.length > 0 && firstColumnValues.length > 0 ? (
          <>
            <div className={`main-content editor-mode-${editorMode}`}>
              {editorMode === 'design' && (() => {
                const isSelectedBlockLocked: boolean = selectedBlock?.isLocked ?? false;
                const isPanelCollapsed: boolean = isBlockPropertiesCollapsed || isSelectedBlockLocked;

                const handleUnlockBlock = async (): Promise<void> => {
                  if (!selectedBlock || selectedBlockIndex === null) return;
                  const updatedBlocks: Block[] = blocks.map((b: Block, i: number) =>
                    i === selectedBlockIndex ? Block.create({ ...b, isLocked: false, dataRows: b.dataRows }) : b
                  );
                  await handleSaveBlocks(updatedBlocks);
                };

                return projectSettingsReadOnly ? null : (
                  <div className={`block-properties${isPanelCollapsed ? ' collapsed' : ''}`} style={{ position: 'relative' }}>
                    <div
                      className="collapse-arrow"
                      onClick={() => {
                        if (!isSelectedBlockLocked) {
                          setIsBlockPropertiesCollapsed(!isBlockPropertiesCollapsed);
                        }
                      }}
                      aria-label={isPanelCollapsed ? t`Expand Block Properties` : t`Collapse Block Properties`}
                      style={isSelectedBlockLocked ? { cursor: 'default', opacity: 0.4 } : undefined}
                    >
                      {isPanelCollapsed
                        ? (isRtlLayout ? <ChevronLeftIcon /> : <ChevronRightIcon />)
                        : (isRtlLayout ? <ChevronRightIcon /> : <ChevronLeftIcon />)
                      }
                    </div>
                    {isPanelCollapsed && isSelectedBlockLocked && (
                      <button
                        className="block-unlock-btn"
                        onClick={handleUnlockBlock}
                        title={t`Unlock block`}
                        aria-label={t`Unlock block`}
                        onMouseEnter={(e) => { e.currentTarget.textContent = '🔑'; }}
                        onMouseLeave={(e) => { e.currentTarget.textContent = '🔒'; }}
                      >
                        🔒
                      </button>
                    )}
                    {!isPanelCollapsed && (
                      <div className="block-properties-body">
                        <BlockConfigForm
                          blockNames={firstColumnValues}
                          onSaveBlocks={handleSaveBlocks}
                          onBlockSelect={setSelectedBlockIndex}
                          selectedBlockIndex={selectedBlockIndex}
                          existingBlocks={blocks}
                          fontFamilies={fontFamilyOptions}
                          isCardsMode={videoSettings.mode === 'cards'}
                          cardGroups={cardGroups}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="block-tabs-wrapper">
                <BlockTabs
                  blocks={blocks}
                  selectedBlockIndex={selectedBlockIndex}
                  onBlockSelect={(index: number | null) => {
                    setSelectedBlockIndex(index);
                    if (index !== null) setScrollToBlockCounter((prev: number) => prev + 1);
                  }}
                  onBlocksChange={handleSaveBlocks}
                  isCardsMode={videoSettings.mode === 'cards'}
                  cardGroups={cardGroups}
                  onSplitCardAt={onSplitCardAt}
                  onMergeCardWithPrevious={onMergeCardWithPrevious}
                  readOnly={projectSettingsReadOnly}
                />
              </div>

              <div className="excel-data">
                {editorMode === 'design' ? (
                  <div className="excel-content-panel">
                    {hasSelectedBlockData ? previewContent : <p>{emptyEditorMessage}</p>}
                  </div>
                ) : (
                  <div
                    ref={setEditWorkspaceElement}
                    className={`editor-edit-workspace${isHorizontalEditLayout ? ' editor-edit-workspace-horizontal' : ''}`}
                    style={{
                      '--workspace-default-preview-pane-ratio': `${DEFAULT_EDIT_PREVIEW_PANE_RATIO * 100}%`,
                      '--edit-preview-pane-min-width': `${EDIT_HORIZONTAL_PREVIEW_MIN_WIDTH}px`,
                      ...(isHorizontalEditLayout
                        ? (editPreviewPaneWidth === null ? {} : {
                          '--edit-preview-pane-width': `${editPreviewPaneWidth}px`,
                        })
                        : (editPreviewPaneHeight === null ? {} : {
                          gridTemplateRows: `minmax(${EDIT_PANE_MIN_HEIGHT}px, 1fr) 12px ${editPreviewPaneHeight}px`,
                        })),
                    } as CSSProperties & Record<'--workspace-default-preview-pane-ratio' | '--edit-preview-pane-min-width' | '--edit-preview-pane-width', string>}
                  >
                    {hasSelectedBlockData ? (
                      <>
                        <div className={`editor-edit-grid-pane${logoConfigContent ? ' editor-edit-logo-pane' : ''}`}>
                          {logoConfigContent ? (
                            <>
                              <div className="editor-edit-logo-grid">{dataGridContent}</div>
                              <div className="editor-edit-logo-config">{logoConfigContent}</div>
                            </>
                          ) : dataGridContent}
                        </div>
                        <div
                          className="editor-edit-divider"
                          role="separator"
                          tabIndex={0}
                          aria-orientation={isHorizontalEditLayout ? 'vertical' : 'horizontal'}
                          aria-valuemin={isHorizontalEditLayout ? EDIT_HORIZONTAL_PREVIEW_MIN_WIDTH : EDIT_PANE_MIN_HEIGHT}
                          aria-valuemax={isHorizontalEditLayout ? editPreviewPaneWidthMaximum : editPreviewPaneMaximum}
                          aria-valuenow={isHorizontalEditLayout ? editPreviewPaneWidthValue : editPreviewPaneValue}
                          aria-label={isHorizontalEditLayout
                            ? t`Resize preview and data grid. Use Arrow Left or Arrow Right to resize.`
                            : t`Resize preview and data grid. Use Arrow Up or Arrow Down to resize.`}
                          onPointerDown={handleEditDividerPointerDown}
                          onKeyDown={handleEditDividerKeyDown}
                        />
                        <div className="editor-edit-preview-pane">
                          {previewContent}
                        </div>
                      </>
                    ) : (
                      <p className="editor-edit-empty-state">{emptyEditorMessage}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <SkeletonEditor label={String(t`Please wait, loading in progress...`)} />
        )}
      </main>
    </div>
  );
}

// Wrapper component that maintains state and provides it to nested routes
function AppContent() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { i18n: linguiI18n } = useLingui();
  const { selectedProject, setSelectedProject } = useProject();
  const canEditData = selectedProject?.permission_tier === 1 || selectedProject?.permission_tier === 2 || selectedProject?.permission_tier === 3;
  const canEdit = selectedProject?.permission_tier === 1 || selectedProject?.permission_tier === 2;
  const location = useLocation();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [originalSheetName, setOriginalSheetName] = useState<string>('');
  const [firstColumnValues, setFirstColumnValues] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>(() => getStoredEditorMode());
  const [videoSettings, setVideoSettings] = useState<VideoSettingsType>(DEFAULT_VIDEO_SETTINGS);
  const videoSettingsRef = useRef<VideoSettingsType>(DEFAULT_VIDEO_SETTINGS);
  const [scrollToBlockCounter, setScrollToBlockCounter] = useState<number>(0);

  const [fonts, setFonts] = useState<FontFamilyInfo[]>(() => cloneFontFamilies(defaultFonts));
  const instantRenderVideoSettings = useMemo(() => withRenderableFonts(videoSettings, fonts), [videoSettings, fonts]);
  const [logos, setLogos] = useState<LogoInfo[]>([]);
  const [accessibleProjectIds, setAccessibleProjectIds] = useState<Set<string> | undefined>(undefined);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState<string | null>(null);
  const [cardGroups, setCardGroups] = useState<CardGroup[]>([]);
  const [defaultCardSettings, setDefaultCardSettings] = useState<DefaultCardSettings>(DEFAULT_CARD_SETTINGS);
  const [isBlockPropertiesCollapsed, setIsBlockPropertiesCollapsed] = useState<boolean>(false);
  const prevProjectIdRef = useRef<string | undefined>(undefined);
  const [isGoogleSheetsLoading, setIsGoogleSheetsLoading] = useState<boolean>(false);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(null);
  const hasAutoLoadedSheetsRef = useRef<boolean>(false);

  const saveProjectTableSnapshot = useCallback(async (snapshot: ProjectTableSaveSnapshot): Promise<void> => {
    if (!canEditData || snapshot.rowDataLength === 0 || snapshot.columnDefs.length === 0 || !snapshot.projectId) {
      return;
    }

    const table = buildTableData(
      snapshot.blocks,
      snapshot.columnDefs,
      snapshot.videoSettings,
      snapshot.cardGroups,
      snapshot.defaultCardSettings
    );
    const userEmail: string | null = auth.currentUser?.email ?? null;
    await saveToGoogleSheets(table, snapshot.originalSheetName, userEmail, snapshot.projectId);
  }, [canEditData]);

  const [saveStatus, setSaveStatus] = useState<ProjectSaveStatus>('idle');
  const activeSaveProjectRef = useRef(selectedProject?.id);
  activeSaveProjectRef.current = selectedProject?.id;
  const projectTableSaveQueue = useMemo(() => new PeriodicProjectTableSaveQueue(saveProjectTableSnapshot, {
    delayMs: 10000,
    maxWaitMs: 15000,
    onError: (error) => console.error('Failed to save to Google Sheets:', error),
    onStateChange: (status) => {
      if (activeSaveProjectRef.current === selectedProject?.id) setSaveStatus(status);
    },
  }), [selectedProject?.id, saveProjectTableSnapshot]);
  useEffect(() => {
    setSaveStatus('idle');
    return () => projectTableSaveQueue.cancel();
  }, [projectTableSaveQueue]);
  const onRetrySave = useCallback(() => { void projectTableSaveQueue.flush().catch(() => undefined); }, [projectTableSaveQueue]);

  useEffect(() => {
    videoSettingsRef.current = videoSettings;
  }, [videoSettings]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setAccessibleProjectIds(undefined);
      return;
    }

    let isMounted = true;
    setAccessibleProjectIds(new Set([selectedProject.id]));

    const loadAccessibleProjects = async (): Promise<void> => {
      try {
        const response = await authFetch(`${getApiUrl()}/projects`);
        if (!response.ok) return;
        const data = await response.json();
        if (!isMounted || data.status !== 'OK' || !Array.isArray(data.projects)) return;
        setAccessibleProjectIds(new Set(data.projects.map((project: { id: string }) => project.id).filter(Boolean)));
        const refreshedProject = data.projects.find((project: { id: string }) => project.id === selectedProject.id);
        if (!refreshedProject) {
          setSelectedProject(null);
        } else if (JSON.stringify(refreshedProject) !== JSON.stringify(selectedProject)) {
          setSelectedProject(refreshedProject);
        }
      } catch (error) {
        console.error('Failed to load accessible projects for asset filtering:', error);
      }
    };

    void loadAccessibleProjects();

    return () => {
      isMounted = false;
    };
  }, [selectedProject, setSelectedProject]);

  useEffect(() => {
    if (!canEditData) projectTableSaveQueue.cancel();
  }, [canEditData, projectTableSaveQueue]);

  useEffect(() => {
    const styleElementId = 'e-credits-font-face-styles';
    const existingStyleElement = document.getElementById(styleElementId);
    const styleElement: HTMLStyleElement = existingStyleElement instanceof HTMLStyleElement
      ? existingStyleElement
      : document.createElement('style');

    styleElement.id = styleElementId;
    styleElement.textContent = buildFontFaceStyles(fonts);
    if (!existingStyleElement) {
      document.head.appendChild(styleElement);
    }

    return () => {
      styleElement.remove();
    };
  }, [fonts]);

  useEffect(() => {
    const pageName: string = location.pathname === '/app/settings'
      ? 'project_settings'
      : location.pathname === '/app/renders'
        ? 'renders'
        : location.pathname === '/app/card_timing'
          ? 'card_timing'
          : location.pathname === '/app'
            ? 'editor'
            : 'unknown';

    trackPageView(pageName, {
      has_data: rowData.length > 0,
      video_mode: videoSettings.mode,
    });
  }, [location.pathname, rowData.length, videoSettings.mode]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }

    trackEvent('project_opened', {}, {
      dedupeKey: selectedProject.id,
      dedupeMs: 4000,
    });
  }, [selectedProject?.id]);

  // Reset all state when project changes (but not on initial mount with same project)
  useEffect(() => {
    const currentProjectId: string | undefined = selectedProject?.id;
    const previousProjectId: string | undefined = prevProjectIdRef.current;
    
    // Only reset if project ID actually changed (not just on remount)
    if (previousProjectId !== undefined && previousProjectId !== currentProjectId) {
      projectTableSaveQueue.cancel();
      setRowData([]);
      setColumnDefs([]);
      setOriginalSheetName('');
      setFirstColumnValues([]);
      setBlocks([]);
      setSelectedBlockIndex(null);
      setVideoSettings(DEFAULT_VIDEO_SETTINGS);
      setDefaultCardSettings(DEFAULT_CARD_SETTINGS);
      setFonts(cloneFontFamilies(defaultFonts));
      setLogos([]);
      setGoogleSheetsUrl(null);
      setCardGroups([]);
      setGoogleSheetsError(null);
      setIsGoogleSheetsLoading(false);
    }

    if (previousProjectId !== currentProjectId) {
      hasAutoLoadedSheetsRef.current = false;
    }

    prevProjectIdRef.current = currentProjectId;
  }, [projectTableSaveQueue, selectedProject?.id]);

  useEffect(() => {
    storeEditorMode(editorMode);
  }, [editorMode]);

  const handleEditorModeChange = useCallback((mode: EditorMode): void => {
    if (editorMode === mode) return;
    setEditorMode(mode);
    trackEvent('editor_mode_changed', { mode });
  }, [editorMode]);

  const resolveCardGroups = useCallback((
    nextBlocks: Block[],
    nextMode: VideoSettingsType['mode'],
    sourceGroups: CardGroup[] = cardGroups
  ): CardGroup[] => {
    if (nextBlocks.length === 0) {
      return [];
    }
    if (sourceGroups.length > 0) {
      return normalizeCardGroups(sourceGroups, nextBlocks);
    }
    if (nextMode === 'cards') {
      return initializeCardGroups(nextBlocks);
    }
    return [];
  }, [cardGroups]);

  const queueProjectTableSave = useCallback((
    nextBlocks: Block[],
    nextSettings: VideoSettingsType,
    nextCardGroups: CardGroup[],
    nextDefaultCardSettings?: DefaultCardSettings
  ): void => {
    if (!canEditData || rowData.length === 0 || columnDefs.length === 0 || !selectedProject?.id) {
      return;
    }

    const snapshot: ProjectTableSaveSnapshot = {
      blocks: nextBlocks,
      videoSettings: nextSettings,
      cardGroups: nextCardGroups,
      defaultCardSettings: nextDefaultCardSettings,
      columnDefs,
      originalSheetName,
      projectId: selectedProject.id,
      rowDataLength: rowData.length,
    };
    setGoogleSheetsError(null);
    projectTableSaveQueue.queue(snapshot);
  }, [canEditData, columnDefs, originalSheetName, projectTableSaveQueue, rowData.length, selectedProject?.id]);

  const handleSplitCardAt = useCallback((blockId: string): void => {
    const nextCardGroups: CardGroup[] = resolveCardGroups(
      blocks,
      videoSettings.mode,
      splitCardAtBlock(cardGroups, blockId, blocks)
    );
    setCardGroups(nextCardGroups);
    queueProjectTableSave(blocks, videoSettings, nextCardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, resolveCardGroups, videoSettings]);

  const handleMergeCardWithPrevious = useCallback((cardId: CardId): void => {
    const nextCardGroups: CardGroup[] = resolveCardGroups(
      blocks,
      videoSettings.mode,
      mergeCardWithPrevious(cardGroups, cardId)
    );
    setCardGroups(nextCardGroups);
    queueProjectTableSave(blocks, videoSettings, nextCardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, resolveCardGroups, videoSettings]);

  const handleFileUpload = useCallback((
    allSheetDataRows: RowData[],
    columnsFromUploader: any[],
    sheetName: string,
    extractedBlocksFromUploader?: Block[],
    videoSettingsFromFile?: VideoSettingsType,
    extractedCardGroups?: CardGroup[],
    extractedDefaultCardSettings?: DefaultCardSettings
  ) => {
    setRowData([]);
    setColumnDefs([]);
    setOriginalSheetName('');
    setFirstColumnValues([]);
    setBlocks([]);

    if (allSheetDataRows.length === 0) {
      console.error('No data rows found in the uploaded file');
      setSelectedBlockIndex(null);
      return;
    }

    const formattedColumns = columnsFromUploader && columnsFromUploader.length > 0
      ? columnsFromUploader.map(colDefFromUploader => ({
          field: colDefFromUploader.field,
          headerName: colDefFromUploader.headerName,
          flex: colDefFromUploader.flex === undefined ? 1 : colDefFromUploader.flex,
          sortable: colDefFromUploader.sortable === undefined ? true : colDefFromUploader.sortable,
          filter: colDefFromUploader.filter === undefined ? true : colDefFromUploader.filter,
          resizable: colDefFromUploader.resizable === undefined ? true : colDefFromUploader.resizable,
        }))
      : [];

    const blockNameSourceField = 'col0';
    const uniqueFirstColValues = Array.from(new Set(
      allSheetDataRows
        .map(row => String(row[blockNameSourceField] || '').trim())
        .filter(name => name && name !== "undefined")
    ));

    setRowData(allSheetDataRows);
    setColumnDefs(formattedColumns);
    setOriginalSheetName(sheetName);
    setFirstColumnValues(uniqueFirstColValues);

    const resolvedDefaultCardSettings: DefaultCardSettings = extractedDefaultCardSettings ?? DEFAULT_CARD_SETTINGS;

    let newBlocksToSet: Block[] = [];
    if (extractedBlocksFromUploader && extractedBlocksFromUploader.length > 0) {
      newBlocksToSet = extractedBlocksFromUploader.filter(block => block.name && block.name.trim() !== '' && block.name !== "undefined");
    } else {
      newBlocksToSet = uniqueFirstColValues.map(name => Block.create({
          name,
          dataRows: [],
          cardFadeInFrames: resolvedDefaultCardSettings.cardFadeInFrames,
          cardDurationFullyUpFrames: resolvedDefaultCardSettings.cardDurationFullyUpFrames,
          cardFadeOutFrames: resolvedDefaultCardSettings.cardFadeOutFrames,
          cardSlugFrames: resolvedDefaultCardSettings.cardSlugFrames,
        }));
    }

    setBlocks(newBlocksToSet);
    setSelectedBlockIndex((previousIndex: number | null) => {
      if (newBlocksToSet.length === 0) return null;
      if (previousIndex === null) return 0;

      const previousBlock: Block | undefined = blocks[previousIndex];
      const matchingIndex: number = previousBlock
        ? newBlocksToSet.findIndex((block: Block) => block.id === previousBlock.id || block.name === previousBlock.name)
        : -1;

      return matchingIndex >= 0 ? matchingIndex : Math.min(previousIndex, newBlocksToSet.length - 1);
    });

    const defaultVideoTime: number = allSheetDataRows.length * 3;
    const normalizedVideoSettings: VideoSettingsType | undefined = videoSettingsFromFile
      ? {
          ...videoSettingsFromFile,
          card_framing: normalizeCardFramingSettings(videoSettingsFromFile.card_framing),
          video_time: videoSettingsFromFile.video_time > 0
            ? videoSettingsFromFile.video_time
            : defaultVideoTime,
        }
      : undefined;

    const vs: VideoSettingsType = normalizedVideoSettings || {
      ...DEFAULT_VIDEO_SETTINGS,
      video_time: defaultVideoTime,
    };
    const nextCardGroups: CardGroup[] = resolveCardGroups(
      newBlocksToSet,
      vs.mode,
      extractedCardGroups ?? []
    );

    setVideoSettings(vs);
    setDefaultCardSettings(resolvedDefaultCardSettings);
    setCardGroups(nextCardGroups);

    setFonts(mergeFontSettingsWithDefaultFamilies(vs.fonts || {}, accessibleProjectIds));

    const logosFromSettings = filterProjectLogos((vs as any).logos || {}, accessibleProjectIds);
    const initialLogos: LogoInfo[] = Object.entries(logosFromSettings).map(([name, logoData]) => ({
      name,
      file: null,
      url: logoData.url,
      enabled: logoData.isEnabled,
    }));
    setLogos(initialLogos);
  }, [accessibleProjectIds, blocks, resolveCardGroups]);

  const processGoogleSheetsResult = useCallback((result: unknown): void => {
    const r = result as {
      success?: boolean;
      data?: unknown[];
      sheetName?: string;
      sheetFormat?: 'legacy' | 'split';
      spreadsheetUrl?: string;
    } | null;

    if (r?.spreadsheetUrl) {
      setGoogleSheetsUrl(r.spreadsheetUrl);
    }

    if (!r?.success || !r.data) throw new Error('Invalid response from server');

    const jsonData: unknown[] = r.data;
    if (!Array.isArray(jsonData) || jsonData.length === 0) throw new Error('No data found in Google Sheets');

    const {
      allSheetDataRows,
      blocks: extractedBlocks,
      videoSettings: extractedVideoSettings,
      columns,
      cardGroups: extractedCardGroups,
      defaultCardSettings: extractedDefaultCardSettings,
    } = extractDataFromRows(jsonData, {
      projectId: selectedProject?.id,
      source: 'google_sheets',
    });

    if (r.sheetFormat === 'legacy') {
      trackEvent('google_sheets_legacy_format_loaded', {
        project_id: selectedProject?.id,
      });
    }

    handleFileUpload(
      allSheetDataRows,
      columns.length ? columns : DEFAULT_COLUMNS,
      r.sheetName || 'Sheet1',
      extractedBlocks.length > 0 ? extractedBlocks : undefined,
      extractedVideoSettings,
      extractedCardGroups,
      extractedDefaultCardSettings
    );

    projectTableSaveQueue.cancel();
  }, [handleFileUpload, projectTableSaveQueue, selectedProject?.id]);

  const loadGoogleSheets = useCallback(async (isManual: boolean = true): Promise<void> => {
    if (!selectedProject?.id) {
      return;
    }

    if (!isManual && (rowData.length > 0 || hasAutoLoadedSheetsRef.current)) {
      return;
    }

    const email = auth.currentUser?.email;
    if (!email) {
      setGoogleSheetsError('No user email available');
      return;
    }

    setIsGoogleSheetsLoading(true);
    setGoogleSheetsError(null);

    if (isManual) {
      try {
        await projectTableSaveQueue.flush();
      } catch (error) {
        setGoogleSheetsError(getErrorMessage(error, 'Failed to save pending changes before loading Google Sheets'));
        setIsGoogleSheetsLoading(false);
        return;
      }
    }

    if (!isManual) {
      hasAutoLoadedSheetsRef.current = true;
    }

    try {
      const params = new URLSearchParams({
        projectId: selectedProject.id,
      });

      const response = await authFetch(`${getApiUrl()}/load-google-sheets?${params}`);

      if (response.status === 404) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.spreadsheetUrl) {
          setGoogleSheetsUrl(errorData.spreadsheetUrl);
        }
        throw new Error(errorData.error || 'Failed to load Google Sheets data');
      }

      const result = await response.json();
      trackEvent('google_sheets_loaded', {
        project_id: selectedProject.id,
        load_mode: isManual ? 'manual' : 'auto',
        sheet_source_kind: 'existing',
        used_template: false,
      });
      processGoogleSheetsResult(result);
    } catch (error) {
      trackEvent('google_sheets_load_failed', {
        project_id: selectedProject.id,
        load_mode: isManual ? 'manual' : 'auto',
      });
      setGoogleSheetsError(getErrorMessage(error, 'Failed to load Google Sheets data'));
      console.error('Error loading from Google Sheets:', error);
    } finally {
      setIsGoogleSheetsLoading(false);
    }
  }, [
    processGoogleSheetsResult,
    projectTableSaveQueue,
    rowData.length,
    selectedProject?.id,
  ]);

  useEffect(() => {
    if (!selectedProject?.id || rowData.length > 0 || hasAutoLoadedSheetsRef.current || isGoogleSheetsLoading) {
      return;
    }

    void loadGoogleSheets(false);
  }, [isGoogleSheetsLoading, loadGoogleSheets, rowData.length, selectedProject?.id]);

  const handleSaveBlocks = useCallback(async (newBlocks: Block[]) => {
    const blocksChanged = newBlocks.length !== blocks.length ||
      newBlocks.some((newBlock, index) => {
        const oldBlock = blocks[index];
        return JSON.stringify(newBlock) !== JSON.stringify(oldBlock);
      });

    if (blocksChanged) {
      const appendedBlocksStartNewCard: boolean = videoSettings.mode === 'cards' &&
        newBlocks.length > blocks.length &&
        blocks.every((block: Block, index: number) => newBlocks[index]?.id === block.id);
      const sourceCardGroups: CardGroup[] | undefined = appendedBlocksStartNewCard
        ? [
            ...resolveCardGroups(blocks, videoSettings.mode),
            {
              id: generateCardId(),
              name: '',
              color: '',
              blockIds: newBlocks.slice(blocks.length).map((block: Block) => block.id),
            },
          ]
        : undefined;
      const nextCardGroups: CardGroup[] = resolveCardGroups(
        newBlocks,
        videoSettings.mode,
        sourceCardGroups
      );

      setBlocks(newBlocks);
      setCardGroups(nextCardGroups);
      queueProjectTableSave(newBlocks, videoSettings, nextCardGroups, defaultCardSettings);
    }
  }, [blocks, defaultCardSettings, queueProjectTableSave, resolveCardGroups, videoSettings]);

  const handleRowDataChange = useCallback(async (newRowData: RowData[]) => {
    if (selectedBlockIndex === null || selectedBlockIndex < 0 || selectedBlockIndex >= blocks.length) {
      return;
    }

    const updatedBlocks = [...blocks];
    updatedBlocks[selectedBlockIndex] = Block.create({
      ...updatedBlocks[selectedBlockIndex],
      dataRows: newRowData
    });
    setBlocks(updatedBlocks);
    queueProjectTableSave(updatedBlocks, videoSettings, cardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, selectedBlockIndex, videoSettings]);

  const handleVideoSettingsChange = useCallback(async (newSettings: VideoSettingsType) => {
    const nextCardGroups: CardGroup[] = resolveCardGroups(blocks, newSettings.mode);
    setVideoSettings(newSettings);
    setCardGroups(nextCardGroups);
    queueProjectTableSave(blocks, newSettings, nextCardGroups, defaultCardSettings);
  }, [blocks, defaultCardSettings, queueProjectTableSave, resolveCardGroups]);

  const handleUpdateCardGroupTiming = useCallback(async (
    cardGroupId: string,
    timingPatch: Partial<CardTimingValues>,
    options?: { persist?: boolean }
  ): Promise<void> => {
    const existingGroup: CardGroup | undefined = cardGroups.find(
      (group: CardGroup) => group.id === cardGroupId
    );
    const fallbackBlockExists: boolean = blocks.some((block: Block) => block.id === cardGroupId);
    const targetGroup: CardGroup | undefined = existingGroup
      ? existingGroup
      : fallbackBlockExists
        ? { id: cardGroupId, name: cardGroupId, color: '', blockIds: [cardGroupId] }
        : undefined;

    if (!targetGroup) return;

    const targetBlockIds: Set<string> = new Set<string>(targetGroup.blockIds);
    const updatedBlocks: Block[] = blocks.map((block: Block) => {
      if (!targetBlockIds.has(block.id)) return block;
      return Block.create({
        ...block,
        cardFadeInFrames: timingPatch.cardFadeInFrames ?? block.cardFadeInFrames ?? 0,
        cardDurationFullyUpFrames: timingPatch.cardDurationFullyUpFrames ?? block.cardDurationFullyUpFrames ?? 0,
        cardFadeOutFrames: timingPatch.cardFadeOutFrames ?? block.cardFadeOutFrames ?? 0,
        cardSlugFrames: timingPatch.cardSlugFrames ?? block.cardSlugFrames ?? 0,
      });
    });

    setBlocks(updatedBlocks);
    if (options?.persist === false) {
      return;
    }
    queueProjectTableSave(updatedBlocks, videoSettings, cardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, videoSettings]);

  const handleApplyTimingToCards = useCallback(async (
    cardGroupIds: string[],
    timingPatch: Partial<CardTimingValues>
  ): Promise<void> => {
    const targetGroupIds: Set<string> = new Set<string>(cardGroupIds);
    const groupsForTiming: Array<{ id: string; blockIds: string[] }> =
      cardGroups.length > 0
        ? cardGroups.map((group: CardGroup) => ({ id: group.id, blockIds: group.blockIds }))
        : blocks.map((block: Block) => ({ id: block.id, blockIds: [block.id] }));
    const targetBlockIds: Set<string> = new Set<string>(
      groupsForTiming
        .filter((group: { id: string; blockIds: string[] }) => targetGroupIds.has(group.id))
        .flatMap((group: { id: string; blockIds: string[] }) => group.blockIds)
    );

    const updatedBlocks: Block[] = blocks.map((block: Block) => {
      if (!targetBlockIds.has(block.id) || block.cardTimingLocked) return block;
      return Block.create({
        ...block,
        cardFadeInFrames: timingPatch.cardFadeInFrames ?? block.cardFadeInFrames ?? 0,
        cardDurationFullyUpFrames: timingPatch.cardDurationFullyUpFrames ?? block.cardDurationFullyUpFrames ?? 0,
        cardFadeOutFrames: timingPatch.cardFadeOutFrames ?? block.cardFadeOutFrames ?? 0,
        cardSlugFrames: timingPatch.cardSlugFrames ?? block.cardSlugFrames ?? 0,
      });
    });

    setBlocks(updatedBlocks);
    queueProjectTableSave(updatedBlocks, videoSettings, cardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, videoSettings]);

  const handleApplyAutoFitToCards = useCallback(async (
    targetFrames: number,
    fadeInFrames: number,
    fadeOutFrames: number,
    slugFrames: number,
    cardGroupIds: string[]
  ): Promise<ApplyAutoFitResult> => {
    const selectedGroupIds: Set<string> = new Set<string>(cardGroupIds);
    const blockById: Map<string, Block> = new Map<string, Block>(blocks.map((block: Block) => [block.id, block]));
    const groupsForTiming: Array<{ id: string; blockIds: string[] }> =
      cardGroups.length > 0
        ? cardGroups
            .filter((group: CardGroup) => group.blockIds.length > 0)
            .map((group: CardGroup) => ({ id: group.id, blockIds: group.blockIds }))
        : blocks.map((block: Block) => ({ id: block.id, blockIds: [block.id] }));
    const selectedUnlockedGroups = groupsForTiming.filter((group: { id: string; blockIds: string[] }) => {
      if (!selectedGroupIds.has(group.id)) return false;
      return !group.blockIds.some((blockId: string) => blockById.get(blockId)?.cardTimingLocked);
    });
    const selectedGroupIdSet: Set<string> = new Set<string>(selectedUnlockedGroups.map((group) => group.id));
    const preservedFrames: number = groupsForTiming.reduce((sum: number, group: { id: string; blockIds: string[] }) => {
      if (selectedGroupIdSet.has(group.id)) return sum;
      const firstBlock: Block | undefined = group.blockIds
        .map((blockId: string) => blockById.get(blockId))
        .find((block: Block | undefined): block is Block => block !== undefined);
      if (!firstBlock) return sum;
      return sum + (
        (firstBlock.cardFadeInFrames ?? 0) +
        (firstBlock.cardDurationFullyUpFrames ?? 0) +
        (firstBlock.cardFadeOutFrames ?? 0) +
        (firstBlock.cardSlugFrames ?? 0)
      );
    }, 0);

    const fitResult = calculateAutoFitTarget(
      selectedUnlockedGroups.length,
      targetFrames,
      fadeInFrames,
      fadeOutFrames,
      slugFrames,
      preservedFrames
    );

    const blockIdsToUpdate: Set<string> = new Set<string>(
      selectedUnlockedGroups.flatMap((group: { id: string; blockIds: string[] }) => group.blockIds)
    );

    const updatedBlocks: Block[] = blocks.map((block: Block) => {
      if (!blockIdsToUpdate.has(block.id) || block.cardTimingLocked) return block;
      return Block.create({
        ...block,
        cardFadeInFrames: Math.max(0, Math.floor(fadeInFrames)),
        cardDurationFullyUpFrames: fitResult.uniformFullyUpFrames,
        cardFadeOutFrames: Math.max(0, Math.floor(fadeOutFrames)),
        cardSlugFrames: Math.max(0, Math.floor(slugFrames)),
      });
    });

    setBlocks(updatedBlocks);
    queueProjectTableSave(updatedBlocks, videoSettings, cardGroups, defaultCardSettings);

    return {
      ...fitResult,
      targetFrames: Math.max(0, Math.floor(targetFrames)),
      preservedFrames,
    };
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, videoSettings]);

  const handleToggleCardGroupLock = useCallback(async (
    cardGroupId: string,
    isLocked: boolean
  ): Promise<void> => {
    const existingGroup: CardGroup | undefined = cardGroups.find(
      (group: CardGroup) => group.id === cardGroupId
    );
    const fallbackBlockExists: boolean = blocks.some((block: Block) => block.id === cardGroupId);
    const targetBlockIds: Set<string> = new Set<string>(
      existingGroup
        ? existingGroup.blockIds
        : fallbackBlockExists
          ? [cardGroupId]
          : []
    );
    if (targetBlockIds.size === 0) return;

    const updatedBlocks: Block[] = blocks.map((block: Block) => {
      if (!targetBlockIds.has(block.id)) return block;
      return Block.create({
        ...block,
        cardTimingLocked: isLocked,
      });
    });

    setBlocks(updatedBlocks);
    queueProjectTableSave(updatedBlocks, videoSettings, cardGroups, defaultCardSettings);
  }, [blocks, cardGroups, defaultCardSettings, queueProjectTableSave, videoSettings]);

  const handleLoadGoogleSheetsFromSidebar = useCallback((): void => {
    void loadGoogleSheets(true);
  }, [loadGoogleSheets]);

  if (location.pathname === '/app/projects') {
    return (
      <ProjectSelector
        onProjectSelect={(project) => {
          setSelectedProject(project);
          navigate('/app', { replace: true });
        }}
      />
    );
  }

  // Show project selector or pre-project template selection if no project is selected
  if (!selectedProject) {
    return (
      <ProjectSelector
        onProjectSelect={(project) => {
          setSelectedProject(project);
        }}
      />
    );
  }

  // Render nested routes (either main app content, renders page, or settings page)
  const isRendersRoute = location.pathname === '/app/renders';
  const isSettingsRoute = location.pathname === '/app/settings';
  const isCardTimingRoute = location.pathname === '/app/card-timing';
  const isGoogleSheetRoute = location.pathname === '/app/sheet';
  const renderBackupAction = (closeMenu: () => void) => rowData.length > 0 ? (
    <ExcelBackupAction
      rowData={rowData}
      columnDefs={columnDefs}
      sheetName={originalSheetName}
      blocks={blocks}
      videoSettings={videoSettings}
      cardGroups={cardGroups}
      defaultCardSettings={defaultCardSettings}
      onClose={closeMenu}
    />
  ) : null;

  if ((isSettingsRoute || isCardTimingRoute) && rowData.length === 0 && isGoogleSheetsLoading) {
    return (
      <div style={{ paddingTop: '60px' }}>
        <NavBar
          editorMode={editorMode}
          onEditorModeChange={handleEditorModeChange}
          showCardTiming={videoSettings.mode === 'cards'}
          canEdit={canEdit}
          googleSheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          isGoogleSheetsLoading={isGoogleSheetsLoading}
          renderBackupAction={renderBackupAction}
          saveStatus={saveStatus}
          onRetrySave={onRetrySave}
        />
        <div className="project-settings-content">
          <SkeletonPage variant="sheet" label={String(t`Please wait, loading in progress...`)}>
            <SkeletonTable columns={7} rows={10} />
          </SkeletonPage>
        </div>
      </div>
    );
  }

  if (isRendersRoute) {
    return (
      <div>
        <NavBar
          editorMode={editorMode}
          onEditorModeChange={handleEditorModeChange}
          showCardTiming={videoSettings.mode === 'cards'}
          canEdit={canEdit}
          googleSheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          isGoogleSheetsLoading={isGoogleSheetsLoading}
          renderBackupAction={renderBackupAction}
          saveStatus={saveStatus}
          onRetrySave={onRetrySave}
        />
        <Renders
          renderControl={canEdit ? (
            <UnifiedRender
              columnDefs={columnDefs}
              sheetName={originalSheetName}
              blocks={blocks}
              isEnabled={true}
              videoSettings={withRenderableFonts(videoSettings, fonts)}
              cardGroups={cardGroups}
            />
          ) : null}
        />
      </div>
    );
  }

  if (isSettingsRoute) {
    return (
      <>
        <ChatbotSidebar
          sheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          canEdit={canEdit}
        />
        <ProjectSettings
          blocks={blocks}
          columnDefs={columnDefs}
          renderVideoSettings={instantRenderVideoSettings}
          editorMode={editorMode}
          onEditorModeChange={handleEditorModeChange}
          showCardTiming={videoSettings.mode === 'cards'}
          renderBackupAction={renderBackupAction}
          saveStatus={saveStatus}
          onRetrySave={onRetrySave}
          googleSheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          isGoogleSheetsLoading={isGoogleSheetsLoading}
          fonts={fonts}
          logos={logos}
          videoSettings={videoSettings}
          onVideoSettingsChange={handleVideoSettingsChange}
          onFontListChange={(updatedFonts) => {
            setFonts(updatedFonts);
            const updatedVideoSettings: VideoSettingsType = {
              ...videoSettingsRef.current,
              fonts: uploadedFontFamiliesToFontSettingsMap(updatedFonts),
            };
            videoSettingsRef.current = updatedVideoSettings;
            setVideoSettings(updatedVideoSettings);
            
            if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
              queueProjectTableSave(blocks, updatedVideoSettings, cardGroups, defaultCardSettings);
            }
          }}
          onLogoListChange={(updatedLogos) => {
            setLogos(updatedLogos);
            // Update video settings with all logos (enabled and disabled)
            const allLogosMap: Record<string, { url: string; isEnabled: boolean }> = {};
            updatedLogos.forEach((logo: LogoInfo) => {
              if (logo.url) {
                allLogosMap[logo.name] = {
                  url: logo.url,
                  isEnabled: logo.enabled
                };
              }
            });
            const updatedVideoSettings = {
              ...videoSettings,
              logos: allLogosMap
            };
            setVideoSettings(updatedVideoSettings);
            if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
              queueProjectTableSave(blocks, updatedVideoSettings, cardGroups, defaultCardSettings);
            }
          }}
          onFontUploaded={(fontName, fontMetadata: FontMetadata) => {
            const currentVideoSettings: VideoSettingsType = videoSettingsRef.current;
            const storedFontMetadata: FontMetadata = fontMetadataForStorage(fontMetadata);
            const updatedVideoSettings: VideoSettingsType = {
              ...currentVideoSettings,
              fonts: { ...(currentVideoSettings.fonts || {}), [fontName]: storedFontMetadata },
            };
            videoSettingsRef.current = updatedVideoSettings;
            setVideoSettings(updatedVideoSettings);
            if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
              queueProjectTableSave(blocks, updatedVideoSettings, cardGroups, defaultCardSettings);
            }
          }}
          onLogoUploaded={(logoName, serverUrl) => {
            setVideoSettings((prev: VideoSettingsType) => ({
              ...prev,
              logos: { 
                ...(prev.logos || {}), 
                [logoName]: { url: serverUrl, isEnabled: true }
              }
            }));
          }}
          onFontDeleted={(fontName) => {
            // Remove font from local state
            setFonts((prev: FontFamilyInfo[]) =>
              prev
                .map((fontFamily: FontFamilyInfo) => ({
                  ...fontFamily,
                  fonts: fontFamily.fonts.filter((font: FontInfo) => font.name !== fontName),
                }))
                .filter((fontFamily: FontFamilyInfo) => fontFamily.fonts.length > 0)
            );
            // Remove from video settings
            setVideoSettings((prev: VideoSettingsType) => {
              const newFonts = { ...(prev.fonts || {}) };
              delete newFonts[fontName];
              return { ...prev, fonts: newFonts };
            });
          }}
          onLogoDeleted={(logoName) => {
            // Remove logo from local state
            setLogos(prev => prev.filter(l => l.name !== logoName));
            // Remove from video settings
            setVideoSettings((prev: VideoSettingsType) => {
              const newLogos = { ...(prev.logos || {}) };
              delete newLogos[logoName];
              return { ...prev, logos: newLogos };
            });
          }}
        />
      </>
    );
  }

  if (isCardTimingRoute) {
    if (!canEdit) return <Navigate to="/app" replace />;
    return (
      <>
        <ChatbotSidebar
          sheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          canEdit={canEdit}
        />
        <CardTiming
          editorMode={editorMode}
          onEditorModeChange={handleEditorModeChange}
          videoMode={videoSettings.mode}
          fps={videoSettings.fps_num}
          blocks={blocks}
          cardGroups={cardGroups}
          onUpdateCardGroupTiming={handleUpdateCardGroupTiming}
          onApplyTimingToCards={handleApplyTimingToCards}
          onApplyAutoFitToCards={handleApplyAutoFitToCards}
          onToggleCardGroupLock={handleToggleCardGroupLock}
          googleSheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          isGoogleSheetsLoading={isGoogleSheetsLoading}
          canEdit={canEdit}
          renderBackupAction={renderBackupAction}
          saveStatus={saveStatus}
          onRetrySave={onRetrySave}
        />
      </>
    );
  }

  if (isGoogleSheetRoute) {
    return (
      <>
        <ChatbotSidebar sheetsUrl={googleSheetsUrl} onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar} canEdit={canEdit} />
        <NavBar
          editorMode={editorMode}
          onEditorModeChange={handleEditorModeChange}
          showCardTiming={videoSettings.mode === 'cards'}
          canEdit={canEdit}
          googleSheetsUrl={googleSheetsUrl}
          onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
          isGoogleSheetsLoading={isGoogleSheetsLoading}
          renderBackupAction={renderBackupAction}
          saveStatus={saveStatus}
          onRetrySave={onRetrySave}
        />
        <GoogleSheetView googleSheetsUrl={googleSheetsUrl} />
      </>
    );
  }

  // Otherwise render the main app content
  return (
    <>
      <ChatbotSidebar
        sheetsUrl={googleSheetsUrl}
        onLoadGoogleSheets={handleLoadGoogleSheetsFromSidebar}
        canEdit={canEdit}
        selectedTarget={selectedBlockIndex !== null && blocks[selectedBlockIndex] ? {
          block: blocks[selectedBlockIndex].name,
          cardNumber: videoSettings.mode === 'cards'
            ? (() => { const index = cardGroups.findIndex(group => group.blockIds.includes(blocks[selectedBlockIndex].id)); return index >= 0 ? index + 1 : null; })()
            : null,
        } : null}
      />
      <MainAppContent
        rowData={rowData}
        columnDefs={columnDefs}
        originalSheetName={originalSheetName}
        firstColumnValues={firstColumnValues}
        blocks={blocks}
        selectedBlockIndex={selectedBlockIndex}
        videoSettings={videoSettings}
        defaultCardSettings={defaultCardSettings}
        fonts={fonts}
        logos={logos}
        googleSheetsUrl={googleSheetsUrl}
        isGoogleSheetsLoading={isGoogleSheetsLoading}
        googleSheetsError={googleSheetsError}
        isBlockPropertiesCollapsed={isBlockPropertiesCollapsed}
        handleSaveBlocks={handleSaveBlocks}
        handleRowDataChange={handleRowDataChange}
        handleVideoSettingsChange={handleVideoSettingsChange}
        setSelectedBlockIndex={setSelectedBlockIndex}
        scrollToBlockCounter={scrollToBlockCounter}
        setScrollToBlockCounter={setScrollToBlockCounter}
        setBlocks={setBlocks}
        setIsBlockPropertiesCollapsed={setIsBlockPropertiesCollapsed}
        onLoadGoogleSheets={() => loadGoogleSheets(true)}
        setVideoSettings={setVideoSettings}
        cardGroups={cardGroups}
        onSplitCardAt={handleSplitCardAt}
        onMergeCardWithPrevious={handleMergeCardWithPrevious}
        readOnly={!canEditData}
        projectSettingsReadOnly={!canEdit}
        editorMode={editorMode}
        onEditorModeChange={handleEditorModeChange}
        renderBackupAction={renderBackupAction}
        saveStatus={saveStatus}
        onRetrySave={onRetrySave}
        projectId={selectedProject?.id}
      />
    </>
    );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const isProductionHost = window.location.hostname === 'fintitles.com'
    || window.location.hostname === 'www.fintitles.com';

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user: any) => {
      setIsAuthenticated(!!user);
    });
    return () => unsub();
  }, []);
  
  // NOTE: These wrappers are used inside the Router (provided by index.tsx)

  const LoginWrapper = () => {
    if (isAuthenticated === null) return <SkeletonPage variant="auth" label="Checking your session" />;
    if (isAuthenticated) return <Navigate to="/app/projects" replace />;
    return <Login />;
  };

  const AppWrapper = () => {
    // If we don't know auth state yet, avoid rendering (prevents flash)
    if (isAuthenticated === null) return null;
    // Protect /app route using AuthGate (assumes AuthGate will redirect to /login if not auth)
    return (
      <AuthGate>
        <AppContent />
      </AuthGate>
    );
  };

  const AdminWrapper = () => {
    if (isAuthenticated === null) return null;
    return (
      <AuthGate>
        <AdminBackOffice />
      </AuthGate>
    );
  };

  return (
    <I18nProvider i18n={i18n}>
      <AppLocaleProvider>
      <ProjectProvider>
        <AppStatsigProvider>
        {/* IMPORTANT: your project should provide a single BrowserRouter at the root.
            Typically index.tsx contains: <BrowserRouter><App /></BrowserRouter>
            This component assumes the Router is already active. */}
        <Routes>
          <Route path="/" element={isAuthenticated === null
            ? <SkeletonPage variant="auth" label="Checking your session" />
            : isAuthenticated
              ? <Navigate to="/app/projects" replace />
              : isProductionHost ? <Navigate to="/login" replace /> : <Landing />} />

          {/* Login - if already authed, open the project selector */}
          <Route path="/login" element={<LoginWrapper />} />

          {/* Main app route (protected by AuthGate) - now handles /app and top-level app pages */}
          <Route path="/app" element={<AppWrapper />} />
          <Route path="/app/projects" element={<AppWrapper />} />
          <Route path="/app/renders" element={<AppWrapper />} />
          <Route path="/app/settings" element={<AppWrapper />} />
          <Route path="/app/card-timing" element={<AppWrapper />} />
          <Route path="/app/sheet" element={<AppWrapper />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/admin" element={<AdminWrapper />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AppStatsigProvider>
      </ProjectProvider>
      </AppLocaleProvider>
    </I18nProvider>
  );
}

export default App;
